import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';

// ── Font size steps ────────────────────────────────────────────────────────────
// 5 discrete steps covering a comfortable reading range (S → XL).
// The prose <article> element receives this value as an inline font-size,
// and the @tailwindcss/typography plugin scales all em-based children
// (headings, code, lists) proportionally.
export const FONT_SIZE_STEPS = [
  '0.875rem', // 0 — Smaller (14 px)
  '1rem',     // 1 — Small   (16 px)
  '1.125rem', // 2 — Normal  (18 px)  ← default
  '1.25rem',  // 3 — Large   (20 px)
  '1.375rem', // 4 — Larger  (22 px)
] as const;

const DEFAULT_INDEX = 2;
const STORAGE_KEY = 'jslipi-font-size';

// ── Context shape ──────────────────────────────────────────────────────────────
interface ReadingPreferencesContextValue {
  fontSizeIndex: number;
  fontSizeValue: string;
  increaseFontSize: () => void;
  decreaseFontSize: () => void;
  canIncrease: boolean;
  canDecrease: boolean;
}

const ReadingPreferencesContext = createContext<ReadingPreferencesContextValue | null>(null);

// ── Provider ───────────────────────────────────────────────────────────────────
export function ReadingPreferencesProvider({ children }: { children: React.ReactNode }) {
  const [fontSizeIndex, setFontSizeIndex] = useState<number>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored !== null) {
        const parsed = parseInt(stored, 10);
        if (!isNaN(parsed) && parsed >= 0 && parsed < FONT_SIZE_STEPS.length) {
          return parsed;
        }
      }
    } catch {
      // localStorage unavailable (e.g. SSR or private mode edge case)
    }
    return DEFAULT_INDEX;
  });

  // Persist on every change
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, String(fontSizeIndex));
    } catch {
      // Ignore write failures
    }
  }, [fontSizeIndex]);

  const increaseFontSize = useCallback(() => {
    setFontSizeIndex(i => Math.min(i + 1, FONT_SIZE_STEPS.length - 1));
  }, []);

  const decreaseFontSize = useCallback(() => {
    setFontSizeIndex(i => Math.max(i - 1, 0));
  }, []);

  return (
    <ReadingPreferencesContext.Provider
      value={{
        fontSizeIndex,
        fontSizeValue: FONT_SIZE_STEPS[fontSizeIndex],
        increaseFontSize,
        decreaseFontSize,
        canIncrease: fontSizeIndex < FONT_SIZE_STEPS.length - 1,
        canDecrease: fontSizeIndex > 0,
      }}
    >
      {children}
    </ReadingPreferencesContext.Provider>
  );
}

// ── Hook ───────────────────────────────────────────────────────────────────────
export function useReadingPreferences(): ReadingPreferencesContextValue {
  const ctx = useContext(ReadingPreferencesContext);
  if (!ctx) {
    throw new Error('useReadingPreferences must be used inside <ReadingPreferencesProvider>');
  }
  return ctx;
}
